import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import { clear, dayLabel, el, hex, icon, lapTime, totalDistance, totalDuration } from './dom.js';
import { CAR_COLORS } from './lobbyScreen.js';
import { serverText, t } from '../i18n/i18n.js';

/**
 * Signing in, in the browser.
 *
 * The whole of it is: ask the server for options, hand them to the platform, post back what
 * it signs. There is no password field, no e-mail, and nothing worth storing in
 * `localStorage` — the session is an HttpOnly cookie the page cannot read, which is the
 * point.
 *
 * **Where this can and cannot work.** WebAuthn needs a secure context, so this is available
 * on `localhost` and on a real deployment and nowhere else. A phone pointed at
 * `http://192.168.x.x` — `npm run dev:lan` — will see the panel say so, and can still race:
 * an account is only ever needed to *author* a tour.
 */

export interface AccountInfo {
  id: string;
  name: string;
  createdAt: number;
}

export interface PasskeyInfo {
  id: string;
  label: string;
  createdAt: number;
  lastUsedAt: number | null;
}

export interface BestTime {
  region: string;
  label: string;
  ms: number;
  setAt: number;
}

export interface AccountStats {
  races: number;
  finishes: number;
  wins: number;
  podiums: number;
  points: number;
  cups: number;
  cupWins: number;
  metres: number;
  raceMs: number;
  firstAt: number;
  best: BestTime[];
}

export interface AccountState {
  account: AccountInfo | null;
  passkeys: PasskeyInfo[];
  stats: AccountStats | null;
  /** False on an insecure origin or an ancient browser: the UI explains rather than fails. */
  supported: boolean;
}

type Listener = (state: AccountState) => void;

interface AuthResponse {
  ok: boolean;
  error?: string;
  account?: AccountInfo | null;
  credentials?: PasskeyInfo[];
  stats?: AccountStats | null;
  options?: unknown;
}

export class AccountSession {
  private state: AccountState = {
    account: null,
    passkeys: [],
    stats: null,
    // `browserSupportsWebAuthn` covers the API being present; `isSecureContext` covers the
    // case that actually happens here, which is a phone on a LAN address.
    supported: browserSupportsWebAuthn() && window.isSecureContext,
  };
  private readonly listeners = new Set<Listener>();

  get current(): AccountState {
    return this.state;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  /** Who the cookie says we are. Safe to call on load; a signed-out answer is not an error. */
  async refresh(): Promise<void> {
    try {
      this.apply(await post('/api/auth/me', undefined, 'GET'));
    } catch {
      // Offline, or the server is not up yet. Staying signed out is the right guess and the
      // next action the player takes will say so properly.
    }
  }

  async register(name: string, label: string): Promise<void> {
    const start = await post('/api/auth/register/options', { name, label });
    const attestation = await startRegistration({ optionsJSON: start.options as never });
    this.apply(await post('/api/auth/register/verify', { response: attestation }));
  }

  async signIn(): Promise<void> {
    const start = await post('/api/auth/login/options');
    // No username: the passkeys are discoverable, so the platform lists whichever it holds
    // for this site and the player picks one.
    const assertion = await startAuthentication({ optionsJSON: start.options as never });
    this.apply(await post('/api/auth/login/verify', { response: assertion }));
  }

  async signOut(): Promise<void> {
    this.apply(await post('/api/auth/logout'));
  }

  async addPasskey(label: string): Promise<void> {
    const start = await post('/api/auth/credentials/options', { label });
    const attestation = await startRegistration({ optionsJSON: start.options as never });
    this.apply(await post('/api/auth/credentials/verify', { response: attestation }));
  }

  async removePasskey(id: string): Promise<void> {
    this.apply(await post(`/api/auth/credentials/${encodeURIComponent(id)}`, undefined, 'DELETE'));
  }

  async deleteAccount(): Promise<void> {
    this.apply(await post('/api/auth/delete'));
  }

  private apply(body: AuthResponse): void {
    if (body.account !== undefined) {
      this.state = {
        ...this.state,
        account: body.account,
        passkeys: body.credentials ?? [],
        stats: body.stats ?? null,
      };
      for (const listener of this.listeners) listener(this.state);
    }
  }
}

async function post(url: string, body?: unknown, method = 'POST'): Promise<AuthResponse> {
  const res = await fetch(url, {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const parsed = (await res.json().catch(() => ({}))) as AuthResponse;
  if (!res.ok || parsed.ok === false) throw new Error(parsed.error ?? t('That did not work. Try again.'));
  return parsed;
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

/** Who is driving: the name on the grid and the colour of the car. Kept in the browser. */
export interface Driver {
  name: string;
  color: number;
}

/**
 * A badge in the corner and the dialog behind it — *you*, in one place.
 *
 * There used to be two: a "Sign in" pill for the account and a "Set your name" chip for
 * the driver, each with its own name field, and nobody could tell which name was which.
 * Now the pill is the driver (colour dot and name), and the dialog has the driver at the
 * top and the account, optional, underneath. Signed in, the driver's name *is* the
 * account's, so there is only ever one name to explain. DECISIONS.md §61.
 *
 * Deliberately the same furniture as the Escape menu — scrim, blur, one centred card —
 * because it is the same kind of thing: a modal that is not part of the race.
 */
export interface AccountPanelOptions {
  /** The current driver, read at paint time — the front door's docket can change it too. */
  getDriver(): Driver;
  /** The driver edited here. The app keeps it, and tells the lobby if there is one. */
  onDriverChange(driver: Driver): void;
  /**
   * Take the player from their own record to everybody's.
   *
   * Wired from outside rather than the panel owning a board of its own: the two are
   * separate dialogs and only the app knows how to swap one for the other.
   */
  onOpenBoard(): void;
}

export class AccountPanel {
  readonly badge: HTMLButtonElement;
  readonly root: HTMLElement;
  private readonly body: HTMLElement;
  private readonly error: HTMLElement;
  private readonly nameInput: HTMLInputElement;
  private readonly nameHint: HTMLElement;
  private readonly swatches = el('div', { class: 'swatches', id: 'driver-swatches' });
  private busy = false;

  constructor(
    private readonly session: AccountSession,
    private readonly options: AccountPanelOptions,
  ) {
    this.badge = el('button', {
      // `corner-badge` is the pill it shares with the leaderboard button next to it;
      // `account-badge` is only the status dot and the signed-in colour.
      class: 'corner-badge account-badge',
      type: 'button',
      id: 'account-badge',
    }) as HTMLButtonElement;
    this.badge.addEventListener('click', () => this.open());

    // Sixteen, the driver's limit, which is the tighter of the two: the same field names
    // the account when one is made from it.
    this.nameInput = el('input', {
      class: 'field',
      type: 'text',
      maxLength: 16,
      placeholder: t('Who is driving?'),
      id: 'driver-name',
      autocomplete: 'username webauthn',
    });
    this.nameInput.addEventListener('change', () => this.commitName());
    this.nameInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') this.commitName();
    });
    this.nameHint = el('p', { class: 'panel-hint driver-name-hint' });
    this.buildSwatches();
    this.error = el('p', { class: 'form-error', id: 'account-error' });
    this.body = el('div', { class: 'account-body' });

    const close = el('button', { class: 'button button-ghost', type: 'button' }, t('Close'));
    close.addEventListener('click', () => this.close());

    this.root = el(
      'div',
      { class: 'modal-scrim account-scrim is-hidden', id: 'account-modal' },
      el(
        'div',
        { class: 'modal-card' },
        el('h2', { class: 'modal-title' }, t('Your driver')),
        el(
          'div',
          { class: 'driver-section' },
          el('label', { class: 'field-label', htmlFor: 'driver-name' }, t('Driver name'), this.nameInput),
          this.nameHint,
          el('div', { class: 'field-label' }, t('Car colour'), this.swatches),
        ),
        el('h3', { class: 'panel-title account-section-title' }, t('Account — optional')),
        this.body,
        this.error,
        el('div', { class: 'modal-actions' }, close),
      ),
    );
    this.root.addEventListener('click', (event) => {
      // Clicking the backdrop closes; clicking inside the card must not.
      if (event.target === this.root) this.close();
    });

    session.subscribe(() => this.paint());
  }

  /**
   * Repaints the pill and the driver fields after the driver changed.
   *
   * Only those, never the account half: a name typed and then committed by clicking
   * "Create an account" fires `change` on the way to the click, and rebuilding the body
   * there would replace the button under the pointer and lose the click.
   */
  refreshDriver(): void {
    this.paintDriver();
  }

  private commitName(): void {
    if (this.session.current.account) return;
    const name = this.nameInput.value.trim().slice(0, 16);
    if (!name || name === this.options.getDriver().name) return;
    this.options.onDriverChange({ ...this.options.getDriver(), name });
  }

  get isOpen(): boolean {
    return !this.root.classList.contains('is-hidden');
  }

  open(): void {
    this.error.textContent = '';
    this.root.classList.remove('is-hidden');
    this.paint();
    if (!this.session.current.account && !this.options.getDriver().name) this.nameInput.focus();
  }

  close(): void {
    // Typing a name and closing without pressing Enter still keeps it.
    this.commitName();
    this.root.classList.add('is-hidden');
  }

  private paintDriver(): void {
    const { account } = this.session.current;
    const driver = this.options.getDriver();
    clear(this.badge);
    this.badge.append(
      el('span', { class: 'driver-chip-dot', style: { background: hex(driver.color) } }),
      driver.name || t('Set your name'),
    );
    this.badge.classList.toggle('is-signed-in', Boolean(account));
    this.badge.title = account
      ? t('Driving as {name}, signed in — your races are recorded', { name: driver.name })
      : t('Your name, your car colour, and an optional account');

    // Signed in, the name is the account's: one name, on the grid and on the board. Out, it
    // is just what you are called today.
    if (document.activeElement !== this.nameInput) this.nameInput.value = driver.name;
    this.nameInput.readOnly = Boolean(account);
    this.nameHint.textContent = account
      ? t('This is your account name, so it is the same on the grid and on the leaderboard.')
      : '';
    this.paintSwatches(driver);
  }

  private paint(): void {
    this.paintDriver();
    const { account, passkeys, supported } = this.session.current;

    clear(this.body);
    if (!supported) {
      this.body.append(
        el(
          'p',
          { class: 'panel-hint' },
          t(
            'Passkeys need a secure connection, so signing in only works on localhost or over https. You can still race and join any lobby from here — an account is only needed to build your own tours.',
          ),
        ),
      );
      return;
    }

    if (!account) {
      this.body.append(
        el(
          'p',
          { class: 'panel-hint' },
          t(
            'Racing never needs one. An account keeps your record and lets you build tours, under the driver name above. No password and no e-mail: your device holds the key and asks you for a fingerprint, face or PIN.',
          ),
        ),
        this.action(
          t('Create an account'),
          async () => {
            const name = this.nameInput.value.trim();
            if (!name) throw new Error(t('Pick a driver name first — the account is made under it.'));
            await this.session.register(name, deviceLabel());
          },
          'button-primary',
        ),
        el('div', { class: 'divider' }, el('span', {}, t('or'))),
        this.action(t('Sign in with a passkey'), () => this.session.signIn()),
      );
      return;
    }

    const list = el('ul', { class: 'passkey-list' });
    for (const passkey of passkeys) {
      const removeLabel = t('Remove {label}', { label: passkey.label });
      const remove = el('button', { class: 'passkey-remove', type: 'button', title: removeLabel }, icon('delete'));
      remove.setAttribute('aria-label', removeLabel);
      remove.addEventListener('click', () => this.run(() => this.session.removePasskey(passkey.id)));
      list.append(
        el(
          'li',
          { class: 'passkey-row' },
          el('span', { class: 'passkey-label' }, passkey.label),
          el(
            'span',
            { class: 'passkey-when' },
            passkey.lastUsedAt
              ? t('used {when}', { when: dayLabel(passkey.lastUsedAt) })
              : t('added {when}', { when: dayLabel(passkey.createdAt) }),
          ),
          remove,
        ),
      );
    }

    this.body.append(
      el('p', { class: 'account-who' }, t('Signed in as '), el('strong', {}, account.name)),
      this.statsBlock(),
      el(
        'p',
        { class: 'panel-hint' },
        passkeys.length > 1
          ? t('Any of these will sign you in.')
          : t('Add a second passkey on another device — if you lose this one, there is no other way back in.'),
      ),
      list,
      el(
        'div',
        { class: 'account-actions' },
        this.action(t('Add a passkey'), () => this.session.addPasskey(deviceLabel())),
        this.action(t('Sign out'), () => this.session.signOut()),
      ),
    );
  }

  /**
   * Built once and only ever re-highlighted. Clicking a swatch blurs the name field first,
   * which commits the name and repaints — and a swatch rebuilt in that repaint is no longer
   * the element the click lands on, so the colour silently did not change.
   */
  private buildSwatches(): void {
    for (const color of CAR_COLORS) {
      const button = el('button', {
        class: 'swatch-button',
        type: 'button',
        style: { background: hex(color) },
        title: t('Car colour {hex}', { hex: hex(color) }),
        dataset: { color: String(color) },
      });
      button.addEventListener('click', () => {
        this.options.onDriverChange({ ...this.options.getDriver(), color });
      });
      this.swatches.append(button);
    }
  }

  private paintSwatches(driver: Driver): void {
    for (const button of this.swatches.querySelectorAll<HTMLElement>('.swatch-button')) {
      button.classList.toggle('is-active', Number(button.dataset.color) === driver.color);
    }
  }

  /** "…and here is how that compares", which is the next thing anybody asks. */
  private boardLink(): HTMLElement {
    const button = el(
      'button',
      { class: 'button button-ghost board-link', type: 'button', id: 'account-board-link' },
      t('See the global board'),
    );
    button.addEventListener('click', () => {
      this.close();
      this.options.onOpenBoard();
    });
    return button;
  }

  /**
   * The record.
   *
   * Only races run while signed in are in here, which is worth saying out loud the first
   * time somebody looks: a table of zeroes after an evening of racing as a guest is
   * otherwise indistinguishable from a bug.
   */
  private statsBlock(): HTMLElement {
    const stats = this.session.current.stats;
    const block = el('div', { class: 'stats-block', id: 'account-stats' });
    if (!stats || stats.races === 0) {
      block.append(
        el(
          'p',
          { class: 'panel-hint' },
          t(
            'No races on record yet. Anything you race while signed in counts — points, wins and your best time in each region, and a place on the global board.',
          ),
        ),
        this.boardLink(),
      );
      return block;
    }

    const grid = el('dl', { class: 'stats-grid' });
    const add = (label: string, value: string, title?: string) => {
      grid.append(
        el('div', { class: 'stat', ...(title ? { title } : {}) }, el('dt', {}, label), el('dd', { class: 'tabular' }, value)),
      );
    };
    add(t('Points'), String(stats.points), t('Cup points, awarded for single races too'));
    add(t('Wins'), String(stats.wins));
    add(t('Podiums'), String(stats.podiums), t('Top three in a field of at least three'));
    add(t('Races'), String(stats.races));
    if (stats.cups) add(t('Cups won'), `${stats.cupWins} / ${stats.cups}`);
    // Finishing is not a given in a game where you can drive into a canal.
    add(t('Finished'), `${Math.round((stats.finishes / stats.races) * 100)}%`);
    add(t('Distance'), totalDistance(stats.metres));
    add(t('Time driven'), totalDuration(stats.raceMs));
    block.append(grid);

    if (stats.best.length) {
      const list = el('ul', { class: 'best-list' });
      for (const best of stats.best.slice(0, 5)) {
        list.append(
          el(
            'li',
            { class: 'best-row' },
            el('span', { class: 'best-where' }, best.label),
            el('span', { class: 'best-time tabular' }, lapTime(best.ms)),
          ),
        );
      }
      block.append(el('h3', { class: 'stats-heading' }, t('Best times')), list);
    }
    block.append(this.boardLink());
    return block;
  }

  private action(label: string, run: () => Promise<void>, extra = ''): HTMLButtonElement {
    const button = el('button', { class: `button ${extra}`.trim(), type: 'button' }, label) as HTMLButtonElement;
    button.addEventListener('click', () => this.run(run));
    return button;
  }

  /**
   * One in-flight ceremony at a time.
   *
   * The platform dialog is modal to the browser, not to this page, and a second
   * `navigator.credentials` call while one is open aborts the first with an error that reads
   * like a failure the player caused.
   */
  private async run(action: () => Promise<void>): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.error.textContent = '';
    this.root.classList.add('is-busy');
    try {
      await action();
    } catch (err) {
      this.error.textContent = readable(err);
    } finally {
      this.busy = false;
      this.root.classList.remove('is-busy');
    }
  }
}

/**
 * What the passkey gets called in the list.
 *
 * A guess from the user agent, and a rough one on purpose: it exists so that somebody with
 * a laptop and a phone can tell which row is which, not to fingerprint anybody.
 */
function deviceLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'iPhone';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac OS X/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Linux/.test(ua)) return 'Linux';
  return t('Passkey');
}

/** Cancelling a passkey prompt is a decision, not an error worth shouting about. */
function readable(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  if (/NotAllowedError|timed out|not allowed/i.test(message)) return t('Cancelled.');
  if (/InvalidStateError|already registered/i.test(message)) {
    return t('This device already has a passkey for this account.');
  }
  return serverText(message);
}

